import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { CustomerWrite, InputMaxLength } from "types"
import TextInput from "components/inputs/textInput"
import TextAreaInput from "components/inputs/textareaInput"
import useStorages from "hooks/useStorages"
import useStorageLimits from "hooks/useStorageLimits"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import Section from "components/layout/section"
import Button from "components/inputs/button"
import useLayout from "hooks/useLayout"
import { logger } from "utils/secureLogger"
import ProcessingButton from "components/inputs/processingButton"
import Form from "components/forms/form"
import FormErrors from "components/formParts/formErrors"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import useCustomers from "hooks/useCustomers"

interface CreateCustomerFormProps {
    storageId: string
    onSuccess?: (res: any) => void
}   

const CreateCustomerForm: React.FC<CreateCustomerFormProps> = ({ storageId, onSuccess }) => {

    const { t } = useTranslation()

    // React hooks.

    // Component state.
    const [customerId, setCustomerId] = useState<number>(1)
    const [name, setName] = useState(``)
    const [email, setEmail] = useState(``)
    const [phone, setPhone] = useState(``)
    const [notes, setNotes] = useState(``)

    // Form status.
    const [errors, setErrors] = useState<string[]>([]);

    // Layout hooks.
    const { addToast } = useLayout()

    const {
        lastAddedCustomerId
    } = useStorages({ storageId })

    const { createCustomerAsync, isCreating } = useCustomers({ storageId })
    
    const { storage } = useStorages({ storageId })
    
    // Check account limits
    const storagePlan = storage?.settings?.storagePlan
    const { canUseCustomers } = useStorageLimits({ storagePlan })

    // Set initial customer ID
    useEffect(() => {
        setCustomerId(lastAddedCustomerId + 1 || 1)
    }, [lastAddedCustomerId])

    // Handle submit.
    const handleSubmit = async (event: React.FormEvent) => {
        logger.info('Create customer form submitted successfully')
        event.preventDefault()
        setErrors([])
        
        if (!storageId) {
            setErrors([t('errorNoStorageId')])
            return
        }
        
        // Check if user can use customers feature
        if (!canUseCustomers) {
            setErrors([t('errorFeatureRequiresProPlan')])
            addToast({ text: t('errorFeatureRequiresProPlan'), colorScheme: 'red' })
            return
        }

        try {
            const customerData: CustomerWrite = {
                ids: {
                    storageId
                },
                _version: 1,
                customerId: customerId,
                name,
                notes,
                contactInformation: {
                    email,
                    phone,
                }
            }
            console.log("customerData: ", customerData)
            const result = await createCustomerAsync(customerData) as { id?: string; path?: string }
            console.log("result: ", result)
            const createdCustomerId = result.id || result.path?.split('/').pop()
            logger.info('Customer created successfully', { customerData, customerId: createdCustomerId })
            addToast({ text: t('toastCustomerCreated'), colorScheme: 'green' })
            if (onSuccess) onSuccess(createdCustomerId)
        } catch (error: unknown) {
            logger.error('Failed to create customer', error)
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
        }
    }

    // Handle ID change.
    const handleIdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const numericValue = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 1
        setCustomerId(numericValue)
    }

    // Handle name change.
    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setName(e.target.value)
    }

    // Handle email change.
    const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setEmail(e.target.value)
    }

    // Handle phone change.
    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setPhone(e.target.value)
    }

    // Handle notes change.
    const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(e.target.value)
    }

    // Render form.
    return (
        <Form onSubmit={handleSubmit}>
            <Section>
                <TextInput
                    label={t('labelCustomerId')}
                    value={customerId.toString()}
                    onChange={handleIdChange}
                    required
                    maxLength={InputMaxLength.short}
                />
                <TextInput
                    label={t('labelCustomerName')}
                    value={name}
                    onChange={handleNameChange}
                    required
                    maxLength={InputMaxLength.short}
                />
                <TextInput
                    label={t('labelCustomerEmail')}
                    value={email}
                    onChange={handleEmailChange}
                    maxLength={InputMaxLength.medium}
                />
                <TextInput
                    label={t('labelCustomerPhone')}
                    value={phone}
                    onChange={handlePhoneChange}
                    maxLength={InputMaxLength.short}
                />
                <TextAreaInput
                    label={t('labelCustomerNotes')}
                    value={notes}
                    onChange={handleNotesChange}
                    maxLength={InputMaxLength.extraLong}
                />
            </Section>
            
            <Section>
                <div className="input-holder">
                    {isCreating ? (
                        <ProcessingButton design="primary" />
                    ) : (
                        <Button wideOnMobile type="submit" design="primary" disabled={!name.trim() || !canUseCustomers}>
                            <AddIcon />
                            <span>{t('buttonCreateCustomer')}</span>
                        </Button>
                    )}
                </div>
            </Section>
            
            <FormErrors errors={errors} />
        </Form>
    )
}

export default CreateCustomerForm
